import React from 'react';
import { Program } from '../types';
import { programService } from '../services/programService';

interface ActiveProgramsPanelProps {
  programs: Program[];
  onViewRoadmap: (program: Program) => void;
  onPauseProgram: (programId: string) => void;
  onResumeProgram: (programId: string) => void;
  onDeleteProgram: (programId: string) => void;
}

const ActiveProgramsPanel: React.FC<ActiveProgramsPanelProps> = ({
  programs,
  onViewRoadmap,
  onPauseProgram,
  onResumeProgram,
  onDeleteProgram,
}) => {
  if (programs.length === 0) {
    return (
      <div className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-8 text-center"
        style={{
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
        }}
      >
        <div className="text-6xl mb-4">🎯</div>
        <div className="font-orbitron text-xl text-gray-400 mb-2">No Active Programs</div>
        <div className="text-sm text-gray-500 font-rajdhani">
          Create your first training program to start your journey!
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {programs.map(program => {
        const stats = programService.getProgramStats(program);
        const daysRemaining = programService.getDaysRemaining(program);
        const nextMilestone = programService.getNextMilestone(program);
        const scheduleDesc = programService.getScheduleDescription(program.schedule);
        const currentPhase = programService.getCurrentPhase(program);

        return (
          <div
            key={program.id}
            className="bg-gradient-to-b from-slate-800/50 via-slate-900/50 to-slate-950/50 backdrop-blur-md border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-2xl overflow-hidden transition-all flex flex-col hover:border-blue-500/50 hover:scale-105 relative"
            style={{
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
            }}
          >
            {/* Header */}
            <div className="p-4 bg-gradient-to-b from-slate-800/60 via-slate-900/50 to-slate-950/60 backdrop-blur-sm border-b border-b-slate-500/30"
              style={{
                boxShadow: 'inset 0 2px 4px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
              }}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-orbitron font-bold text-base mb-1 truncate" title={program.title} style={{ color: 'var(--color-primary)' }}>
                    {program.title}
                  </h3>
                  <div className="flex flex-wrap items-center gap-2 text-xs font-rajdhani text-gray-400">
                    <span className="whitespace-nowrap">📅 {scheduleDesc}</span>
                    <span>•</span>
                    <span className="whitespace-nowrap">💪 {program.intensity.charAt(0).toUpperCase() + program.intensity.slice(1)}</span>
                  </div>
                </div>
                <div 
                  className={`px-3 py-1.5 rounded-full text-xs font-bold font-orbitron shrink-0 border-t-2 border-l-2 border-r-2 border-b-2 backdrop-blur-sm`}
                  style={
                    program.status === 'active'
                      ? {
                          background: 'linear-gradient(to bottom, rgba(34, 197, 94, 0.2), rgba(34, 197, 94, 0.15))',
                          color: 'rgb(74, 222, 128)',
                          borderColor: 'rgba(74, 222, 128, 0.5)',
                          borderBottomColor: 'rgba(34, 197, 94, 0.7)',
                          borderRightColor: 'rgba(34, 197, 94, 0.7)',
                          boxShadow: '0 2px 8px rgba(34, 197, 94, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1)'
                        }
                      : program.status === 'paused'
                      ? {
                          background: 'linear-gradient(to bottom, rgba(234, 179, 8, 0.2), rgba(234, 179, 8, 0.15))',
                          color: 'rgb(250, 204, 21)',
                          borderColor: 'rgba(250, 204, 21, 0.5)',
                          borderBottomColor: 'rgba(234, 179, 8, 0.7)',
                          borderRightColor: 'rgba(234, 179, 8, 0.7)',
                          boxShadow: '0 2px 8px rgba(234, 179, 8, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1)'
                        }
                      : {
                          background: 'linear-gradient(to bottom, rgba(107, 114, 128, 0.2), rgba(107, 114, 128, 0.15))',
                          color: 'rgb(156, 163, 175)',
                          borderColor: 'rgba(156, 163, 175, 0.5)',
                          borderBottomColor: 'rgba(107, 114, 128, 0.7)',
                          borderRightColor: 'rgba(107, 114, 128, 0.7)',
                          boxShadow: '0 2px 8px rgba(107, 114, 128, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1)'
                        }
                  }
                >
                  {program.status === 'active' ? 'ACTIVE' : program.status === 'paused' ? 'PAUSED' : 'DONE'}
                </div>
              </div>
            </div>

            {/* Progress */}
            <div className="p-4 flex-1 flex flex-col">
              {/* Progress Bar */}
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-rajdhani text-gray-400">
                    Phase {currentPhase}/{program.totalPhases}
                  </span>
                  <span className="text-sm font-bold font-orbitron text-cyan-400">
                    {program.progress}%
                  </span>
                </div>
                <div className="w-full rounded-full h-3 overflow-hidden bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 transition-all duration-500"
                  style={{
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  <div
                    className="h-full transition-all duration-500 relative"
                    style={{ 
                      width: `${program.progress}%`,
                      background: `linear-gradient(to right, var(--color-primary), var(--color-secondary), var(--color-primary))`
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-30 animate-pulse" />
                  </div>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-xl p-3"
                  style={{
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  <div className="text-xs text-gray-400 font-rajdhani uppercase">Quests</div>
                  <div className="text-base font-bold font-orbitron text-white">
                    {program.questsCompleted}/{stats.totalQuestsExpected}
                  </div>
                </div>
                <div className="bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-xl p-3"
                  style={{
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  <div className="text-xs text-gray-400 font-rajdhani uppercase">Days</div>
                  <div className="text-base font-bold font-orbitron text-cyan-400">
                    {daysRemaining}
                  </div>
                </div>
                <div className="bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-xl p-3"
                  style={{
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  <div className="text-xs text-gray-400 font-rajdhani uppercase">Goals</div>
                  <div className="text-base font-bold font-orbitron text-blue-400">
                    {stats.milestonesCompleted}/{program.milestones.length}
                  </div>
                </div>
              </div>

              {/* Current Phase Info */}
              {nextMilestone && (
                <div className="bg-gradient-to-b from-blue-900/30 via-blue-950/20 to-blue-900/30 backdrop-blur-sm border-t-2 border-l-2 border-blue-500/40 border-r-2 border-b-2 border-r-blue-700/60 border-b-blue-700/60 rounded-2xl p-3 mb-3"
                  style={{
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                  }}
                >
                  <div className="text-xs font-rajdhani font-bold mb-0.5 text-blue-400">
                    🎯 NEXT MILESTONE
                  </div>
                  <div className="text-sm font-orbitron text-white font-bold line-clamp-1" title={nextMilestone.title}>
                    {nextMilestone.title}
                  </div>
                  <div className="text-xs text-gray-400 font-rajdhani mt-0.5 line-clamp-2" title={nextMilestone.description}>
                    {nextMilestone.description}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 mt-auto">
                <button
                  onClick={() => onViewRoadmap(program)}
                  className="flex-1 py-2.5 bg-gradient-to-b from-blue-800/60 via-purple-800/50 to-blue-950/60 backdrop-blur-sm border-t-2 border-l-2 border-blue-500/50 border-r-2 border-b-2 border-r-purple-700/70 border-b-purple-700/70 rounded-xl font-orbitron font-bold text-xs tracking-wider transition-all text-white hover:scale-105 relative"
                  style={{
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2)'
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-xl"></div>
                  <span className="relative z-10">📍 ROADMAP</span>
                </button>
                {program.status === 'active' ? (
                  <button
                    onClick={() => onPauseProgram(program.id)}
                    className="px-3 py-2.5 bg-gradient-to-b from-yellow-800/40 via-yellow-900/40 to-yellow-950/40 backdrop-blur-sm border-t-2 border-l-2 border-yellow-500/50 border-r-2 border-b-2 border-r-yellow-700/70 border-b-yellow-700/70 rounded-xl font-orbitron font-bold text-xs tracking-wider transition-all text-yellow-400 hover:scale-105 relative"
                    title="Pause program"
                    style={{
                      boxShadow: '0 2px 8px rgba(234, 179, 8, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.1)'
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-xl"></div>
                    <span className="relative z-10">⏸</span>
                  </button>
                ) : program.status === 'paused' ? (
                  <button
                    onClick={() => onResumeProgram(program.id)}
                    className="px-3 py-2.5 bg-gradient-to-b from-green-800/40 via-green-900/40 to-green-950/40 backdrop-blur-sm border-t-2 border-l-2 border-green-500/50 border-r-2 border-b-2 border-r-green-700/70 border-b-green-700/70 rounded-xl font-orbitron font-bold text-xs tracking-wider transition-all text-green-400 hover:scale-105 relative"
                    title="Resume program"
                    style={{
                      boxShadow: '0 2px 8px rgba(34, 197, 94, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.1)'
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-xl"></div>
                    <span className="relative z-10">▶</span>
                  </button>
                ) : null}
                <button
                  onClick={() => {
                    if (window.confirm(`Are you sure you want to delete "${program.title}"? This will also delete all associated quests.`)) {
                      onDeleteProgram(program.id);
                    }
                  }}
                  className="px-3 py-2.5 bg-gradient-to-b from-red-800/40 via-red-900/40 to-red-950/40 backdrop-blur-sm border-t-2 border-l-2 border-red-500/50 border-r-2 border-b-2 border-r-red-700/70 border-b-red-700/70 rounded-xl font-orbitron font-bold text-xs tracking-wider transition-all text-red-400 hover:scale-105 relative"
                  title="Delete program"
                  style={{
                    boxShadow: '0 2px 8px rgba(239, 68, 68, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.1)'
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-xl"></div>
                  <span className="relative z-10">🗑️</span>
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ActiveProgramsPanel;

